/**
 * Toast API Hook
 * Provides access to Toast POS data through the proxy API
 */
import { useState, useCallback } from 'react';
import { post, get } from 'aws-amplify/api';
import { useAuth } from '../contexts/AuthContext';

const useToastAPI = () => {
  const { getAuthToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Call the Toast API proxy
   * @param {string} api - API name (labor, config, menus, orders, restaurants, stock, cash_management)
   * @param {string} method - Method name from the API
   * @param {object} params - Optional parameters for the method
   * @param {object} [opts] - { fresh?: boolean } — set fresh:true to bypass the proxy's in-memory cache
   */
  const callToastAPI = useCallback(async (api, method, params = {}, opts = {}) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      const response = await post({
        apiName: 'ChulasAdminAPI',
        path: '/v1/admin/toast',
        options: {
          headers: { Authorization: token },
          body: { api, method, params, ...(opts.fresh ? { fresh: true } : {}) }
        }
      }).response;

      const data = await response.body.json();

      if (!data.success) {
        throw new Error(data.error || 'Toast API request failed');
      }

      // Busy-day responses (e.g. /orders/v2/ordersBulk) overflow Lambda's
      // 6 MB sync response cap. The proxy uploads those bodies to S3 and
      // returns a short-lived presigned URL in `payloadUrl` instead of
      // inlining `data`. Fetch transparently so callers stay oblivious.
      if (data.payloadUrl) {
        const payloadResp = await fetch(data.payloadUrl);
        if (!payloadResp.ok) {
          throw new Error(`Failed to fetch offloaded Toast payload: ${payloadResp.status}`);
        }
        setLoading(false);
        return await payloadResp.json();
      }

      setLoading(false);
      return data.data;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch Toast data';
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  }, [getAuthToken]);

  // ==================== LABOR API ====================

  const getEmployees = useCallback(async (lastModified = null, opts = {}) => {
    return callToastAPI('labor', 'get_employees', lastModified ? { last_modified: lastModified } : {}, opts);
  }, [callToastAPI]);

  const getEmployee = useCallback(async (guid) => {
    return callToastAPI('labor', 'get_employee', { guid });
  }, [callToastAPI]);

  const getJobs = useCallback(async (lastModified = null, opts = {}) => {
    return callToastAPI('labor', 'get_jobs', lastModified ? { last_modified: lastModified } : {}, opts);
  }, [callToastAPI]);

  const getJob = useCallback(async (guid) => {
    return callToastAPI('labor', 'get_job', { guid });
  }, [callToastAPI]);

  const getShifts = useCallback(async (startDate = null, endDate = null, employeeGuid = null, opts = {}) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (employeeGuid) params.employee_guid = employeeGuid;
    // /labor/v1/shifts only takes startDate/endDate, but the proxy expands
    // a business_date into a 24h window so daily-mode callers stay
    // symmetric with get_time_entries (which accepts business_date
    // natively). Passed through opts to avoid reshuffling the positional
    // signature that ToastLabor.js already relies on.
    if (opts.businessDate) params.business_date = opts.businessDate;
    return callToastAPI('labor', 'get_shifts', params, opts);
  }, [callToastAPI]);

  const getTimeEntries = useCallback(async (startDate = null, endDate = null, businessDate = null, employeeGuid = null, opts = {}) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (businessDate) params.business_date = businessDate;
    if (employeeGuid) params.employee_guid = employeeGuid;
    return callToastAPI('labor', 'get_time_entries', params, opts);
  }, [callToastAPI]);

  const getBreaks = useCallback(async (startDate = null, endDate = null, businessDate = null) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (businessDate) params.business_date = businessDate;
    return callToastAPI('labor', 'get_breaks', params);
  }, [callToastAPI]);

  // ==================== CONFIG API ====================

  const getBreakTypes = useCallback(async () => {
    return callToastAPI('config', 'get_break_types');
  }, [callToastAPI]);

  const getDiningOptions = useCallback(async () => {
    return callToastAPI('config', 'get_dining_options');
  }, [callToastAPI]);

  const getServiceAreas = useCallback(async () => {
    return callToastAPI('config', 'get_service_areas');
  }, [callToastAPI]);

  const getRestaurantServices = useCallback(async () => {
    return callToastAPI('config', 'get_restaurant_services');
  }, [callToastAPI]);

  const getTables = useCallback(async () => {
    return callToastAPI('config', 'get_tables');
  }, [callToastAPI]);

  const getRevenueCenters = useCallback(async () => {
    return callToastAPI('config', 'get_revenue_centers');
  }, [callToastAPI]);

  // ==================== RESTAURANTS API ====================

  const getRestaurant = useCallback(async (restaurantGuid = null) => {
    return callToastAPI('restaurants', 'get_restaurant', restaurantGuid ? { restaurant_guid: restaurantGuid } : {});
  }, [callToastAPI]);

  const getRestaurantLocation = useCallback(async () => {
    return callToastAPI('restaurants', 'get_restaurant_location');
  }, [callToastAPI]);

  const getRestaurantSchedule = useCallback(async () => {
    return callToastAPI('restaurants', 'get_restaurant_schedule');
  }, [callToastAPI]);

  // ==================== ORDERS API ====================

  const getOrders = useCallback(async (businessDate = null, startDate = null, endDate = null, opts = {}) => {
    const params = {};
    if (businessDate) params.business_date = businessDate;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return callToastAPI('orders', 'get_orders', params, opts);
  }, [callToastAPI]);

  const getOrdersBulk = useCallback(async (businessDate = null, startDate = null, endDate = null) => {
    const params = {};
    if (businessDate) params.business_date = businessDate;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return callToastAPI('orders', 'get_orders_bulk', params);
  }, [callToastAPI]);

  // ==================== STOCK API ====================

  const getInventory = useCallback(async (status = null, opts = {}) => {
    return callToastAPI('stock', 'get_inventory', status ? { status } : {}, opts);
  }, [callToastAPI]);

  const getOutOfStockItems = useCallback(async () => {
    return callToastAPI('stock', 'get_out_of_stock_items');
  }, [callToastAPI]);

  // ==================== CASH MANAGEMENT API ====================

  const getCashEntries = useCallback(async (businessDate = null) => {
    return callToastAPI('cash_management', 'get_cash_entries', businessDate ? { business_date: businessDate } : {});
  }, [callToastAPI]);

  const getDeposits = useCallback(async (businessDate = null) => {
    return callToastAPI('cash_management', 'get_deposits', businessDate ? { business_date: businessDate } : {});
  }, [callToastAPI]);

  // ==================== MENUS API ====================

  const getMenus = useCallback(async () => {
    return callToastAPI('menus', 'get_menus');
  }, [callToastAPI]);

  const getMenuGroups = useCallback(async () => {
    return callToastAPI('menus', 'get_menu_groups');
  }, [callToastAPI]);

  const getMenuItems = useCallback(async () => {
    return callToastAPI('menus', 'get_menu_items');
  }, [callToastAPI]);

  const getFullMenu = useCallback(async () => {
    return callToastAPI('menus', 'get_full_menu');
  }, [callToastAPI]);

  return {
    // State
    loading,
    error,

    // Generic call
    callToastAPI,

    // Labor
    getEmployees,
    getEmployee,
    getJobs,
    getJob,
    getShifts,
    getTimeEntries,
    getBreaks,

    // Config
    getBreakTypes,
    getDiningOptions,
    getServiceAreas,
    getRestaurantServices,
    getTables,
    getRevenueCenters,

    // Restaurants
    getRestaurant,
    getRestaurantLocation,
    getRestaurantSchedule,

    // Orders
    getOrders,
    getOrdersBulk,

    // Stock
    getInventory,
    getOutOfStockItems,

    // Cash Management
    getCashEntries,
    getDeposits,

    // Menus
    getMenus,
    getMenuGroups,
    getMenuItems,
    getFullMenu,
  };
};

export default useToastAPI;
